import { v4 as uuid } from 'uuid'

export interface ActivityLogArgument {
  id?: string
  userID: string
  teamID: string
  activityID: string
  amount: number
  value?: number
  challengeID: string
  createdAt?: Date
}

/**
 * Normalizes a number to 3 decimal places and removes trailing zeros
 * Also rounds to nearest whole number if very close (within 0.002) to handle floating point precision errors
 * Example: 6.00000000001 -> 6, 6.100 -> 6.1, 6.001 -> 6.001, 59.998 -> 60, 59.997 -> 60
 */
function normalizeAmount(amount: number): number {
  // Round to 3 decimal places to prevent floating point precision issues
  const rounded = Math.round(amount * 1000) / 1000
  
  // Check if the rounded value is very close to a whole number (within 0.002)
  // This handles cases where floating point errors cause 60 to become 59.998 or 59.997
  const nearestWhole = Math.round(rounded)
  const difference = Math.abs(rounded - nearestWhole)
  
  // If within 0.002 of a whole number, use the whole number
  if (difference <= 0.005) {
    return nearestWhole
  }
  
  // Otherwise, remove trailing zeros by parsing the fixed decimal representation
  return parseFloat(rounded.toFixed(3))
}

export default class ActivityLog {
  readonly id: string
  readonly userID: string
  readonly teamID: string
  readonly activityID: string
  readonly amount: number
  readonly value: number
  readonly challengeID: string
  readonly createdAt: Date

  constructor(arg: ActivityLogArgument) {
    this.id = arg.id ?? uuid()
    this.userID = arg.userID
    this.teamID = arg.teamID
    this.activityID = arg.activityID
    // Normalize amount to remove trailing zeros (6.000 -> 6)
    this.amount = normalizeAmount(arg.amount)
    this.value = normalizeAmount(arg.value ?? arg.amount)
    if (!arg.challengeID) throw new Error('ActivityLog requires challengeID')
    this.challengeID = arg.challengeID
    this.createdAt = arg.createdAt ?? new Date()
  }

  static fromPG(data: any) {
    // Normalize amount from database to remove floating point precision issues and trailing zeros
    const amount = typeof data.amount === 'number' 
      ? normalizeAmount(data.amount)
      : data.amount
    
    return new ActivityLog({
      id: data.id,
      userID: data.user_id,
      teamID: data.team_id,
      activityID: data.activity_id,
      amount,
      value: typeof data.value === 'number' ? normalizeAmount(data.value) : amount,
      challengeID: data.challenge_id,
      createdAt: new Date(data.created_at),
    })
  }

  toPG() {
    return {
      id: this.id,
      user_id: this.userID,
      team_id: this.teamID,
      activity_id: this.activityID,
      amount: this.amount,
      value: this.value,
      challenge_id: this.challengeID,
      created_at: this.createdAt,
    }
  }
}
